import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  Typography,
  CircularProgress,
  Grid,
  Chip,
  Skeleton
} from '@mui/material';
import { req } from './ServerUtils';
import Gallery from './Gallery';
import EmblaCarouselPngs from './CarouselOfPngs';
import { useUser } from './UserProvider';
import { useRef } from 'react';


const INIT_CATS = [{
  name: 'List',
  value: 'listicle'
}, 
// {
//   name: 'Comparison',
//   value: 'a_vs_b'
// }, 
{
  name: 'Carousel',
  value: 'carousel'
}
];
export function TemplateGallery({selectedTemplate, setSelectedTemplate, isOpen, setStep, cats=INIT_CATS}) {
    const [categories, setCategories] = useState(cats);
    const [templates, setTemplates] = useState({});
    const [cachedTemplates, setCachedTemplates] = useState({});
    const [pages, setPages] = useState(() => 
      cats.reduce((acc, cat) => ({
        ...acc,
        [cat.value]: 1
      }), {})
    );
    const [totalPages, setTotalPages] = useState(() => 
      cats.reduce((acc, cat) => ({
        ...acc,
        [cat.value]: 1
      }), {})
    );
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState(cats[0].value);
    const loadingRef = useRef(null);
    const {smallScreen} = useUser();
  
    useEffect(() => {
      if (!isOpen) return;
  
      const currentPage = pages[category];
      if (!cachedTemplates[category] || !cachedTemplates[category][currentPage]) {
        fetchTemplates(currentPage);
      } else {
        // For infinite scroll, we want to append rather than replace
        if (currentPage === 1) {
          setTemplates(prev => ({
            ...prev,
            [category]: cachedTemplates[category][currentPage]
          }));
        } else {
          setTemplates(prev => ({
            ...prev,
            [category]: [...(prev[category] || []), ...cachedTemplates[category][currentPage]]
          }));
        }
      }
    }, [isOpen, category, pages]);
  
    useEffect(() => {
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !loading && pages[category] < totalPages[category]) {
            setPages(prev => ({
              ...prev,
              [category]: prev[category] + 1
            }));
          }
        },
        { threshold: 0.1 }
      );
  
      if (loadingRef.current) {
        observer.observe(loadingRef.current);
      }
  
      return () => observer.disconnect();
    }, [loading, category, totalPages]);
  
    const fetchTemplates = async (currentPage) => {
      setLoading(true);
      try {
        const data = await req(`/html_templates?page=${currentPage}&category=${category}`);
        
        // For infinite scroll, append new templates instead of replacing
        if (currentPage === 1) {
          setTemplates(prev => ({
            ...prev,
            [category]: data.templates
          }));
        } else {
          setTemplates(prev => ({
            ...prev,
            [category]: [...(prev[category] || []), ...data.templates]
          }));
        }
        
        setTotalPages(prev => ({
          ...prev,
          [category]: data.total_pages
        }));
  
        setCachedTemplates(prev => ({
          ...prev,
          [category]: { 
            ...(prev[category] || {}), 
            [currentPage]: data.templates 
          }
        }));
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };
  
    const handleCategoryChange = (newCategory) => {
      setCategory(newCategory);
      // Only reset the specific category if it hasn't been loaded yet
      if (!templates[newCategory]) {
        setPages(prev => ({
          ...prev,
          [newCategory]: 1
        }));
      }
    };
  
    return (
      <Box style={{

        minWidth: 600,
        minHeight: 400,
      }}>
        <div style={{
          display: 'flex',
          gap: smallScreen ? 8 : 20,
          overflowX: 'auto',
          marginBottom: 20,
          textAlign: 'center',
          justifyContent: 'center',
        }}>
          {categories.map((cat, i) => (
            <Chip
              variant='outlined'
              size='large'
              key={i}
              label={cat.name}
              clickable
              style={{
                padding: smallScreen ? 6 : 20,
                borderRadius: 25,
                fontSize: "1rem"
              }}
              color={category === cat.value ? "primary" : "default"}
              onClick={() => handleCategoryChange(cat.value)}
            />
          ))}
        </div>
  
        {(category === "carousel" || category === "mako-carousel") && (
          <CarouselGallery 
            templates={templates[category] || []} 
            itemWidth={200} 
            setSelectedTemplate={setSelectedTemplate} 
            selectedTemplate={selectedTemplate} 
          />
        )}
  
        {category !== "carousel" && category !== "mako-carousel" && (
          <Gallery
            style={{
              overflowY: "scroll",
              width: "100%",
              maxWidth: "100%",
            }}
            data={templates[category] || []}
            render={(template, itemWidth) => (
              <TemplatePreview 
                category={category} 
                template={template} 
                itemWidth={itemWidth} 
                setSelectedTemplate={setSelectedTemplate} 
                selectedTemplate={selectedTemplate} 
              />
            )}
          />
        )}
  
        {/* Loading indicator */}
        <div ref={loadingRef} style={{ height: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {loading && <CircularProgress size={30} />}
        </div>
  
        <div style={{
          position: "absolute",
          bottom: 0,
          backgroundColor: "white",
          height: 30,
          width: "100%",
        }}>
          <Button 
            fullWidth 
            size="large" 
            variant="contained" 
            style={{
              position: "absolute",
              bottom: 10,
              left: 0
            }}  
            onClick={() => setStep("template-selected")}
          >
            Generate
          </Button>
        </div>
      </Box>
    );
  }

function CarouselGallery({templates, setSelectedTemplate, selectedTemplate }) {
    // Using Skeleton while images load
    const [loadedImages, setLoadedImages] = useState({});
  
    const handleImageLoad = (templateId) => {
      setLoadedImages(prev => ({
        ...prev,
        [templateId]: true
      }));
    };
  
    return (
      <div style={{
        display: 'flex',
        gap: 20,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        {templates.map((template, i) => (
          <>
              <div key={i} style={{ 
                position: 'relative', 
                display: loadedImages[template.id] ? 'block' : 'none',
                transition: loadedImages[template.id] ? 'opacity 0.3s ease-in-out' : 'none',
                opacity: loadedImages[template.id] ? 1 : 0
              }}>
                  <EmblaCarouselPngs 
                    images={template.previews} 
                    className="templates-list" 
                    setSelectedTemplate={setSelectedTemplate} 
                    template={template} 
                    selectedTemplate={selectedTemplate}
                    onLoad={() => handleImageLoad(template.id)}
                  /> 
              </div>
            
              <Skeleton 
                variant="rectangular" 
                width="20rem" 
                height="25rem" 
                animation="wave"                  
                style={{ 
                  width: '20rem', 
                  height: '25rem', 
                  display: loadedImages[template.id] ? 'none' : 'block'

                }}
              />
            
          </>
        ))}
      </div>
    );
  }
  

function TemplatePreview({ template, itemWidth, setSelectedTemplate, selectedTemplate }) {
  return (
    <Box
      onClick={() => setSelectedTemplate(template)}
      sx={{
        
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        textAlign: 'center'
      }}
    >
      <img src={template.preview} alt={template.title} style={{ 
        width: itemWidth, 
        height: 'auto',
        border: selectedTemplate?.id === template.id ? '4px solid #8e918e' : '4px solid transparent',
        
      }} />
    </Box>
  );
}
